import { Component, OnDestroy, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CopilotApiService } from '../../services/copilot-api-service';
import { AzugaCarrierListItem, CarrierListElement } from '../../model/carrier';
import { AuthService } from '@auth0/auth0-angular';
import { AddCarrierDialog } from './dialog/add-carrier-dialog';
import { BusinessPartnerSelector } from '../../common/components/business-partner-selector/business-partner-selector';
import { BusinessPartner } from '../../model/business_partner';
import { HosCopilotService } from '../../services/hos-copilot-service';
import { ProviderSelector } from '../../common/components/provider-selector/provider-selector';
import { Provider } from '../../model/provider';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-carriers',
  standalone: true,
  imports: [CommonModule, TranslateModule, AddCarrierDialog, BusinessPartnerSelector, ProviderSelector],
  templateUrl: './carriers.html',
  styleUrl: './carriers.css'
})
export class Carriers implements OnInit, OnDestroy {
  private static readonly MONDO_PROVIDER_ID = 1;
  private static readonly AZUGA_PROVIDER_ID = 2;

  protected readonly currentMode = signal<'mondo' | 'azuga' | null>(null);

  protected readonly carriers = signal<CarrierListElement[] | null>(null);
  protected readonly loading = signal<boolean>(false);
  protected readonly error = signal<string | null>(null);
  protected readonly azugaCarriers = signal<AzugaCarrierListItem[]>([]);
  protected readonly azugaLoading = signal<boolean>(false);
  protected readonly azugaError = signal<boolean>(false);
  protected readonly blockedReason = signal<
    'missing_bp' | 'missing_provider' | null
  >(null);
  protected readonly showDisabled = signal<boolean>(false);
  protected readonly searchTerm = signal<string>('');
  protected readonly sortKey = signal<'Name' | 'ApiKey' | 'Enabled'>('Name');
  protected readonly sortDir = signal<'asc' | 'desc'>('asc');
  protected readonly filteredCarriers = computed(() => {
    const list = this.carriers() || [];
    const q = this.searchTerm().trim().toLowerCase();
    const base = q
      ? list.filter(c =>
          String(c.Name || '').toLowerCase().includes(q) ||
          String(c.ApiKey || '').toLowerCase().includes(q)
        )
      : list;
    const enabledFiltered = this.showDisabled() ? base : base.filter(c => c.Enabled === true);
    const key = this.sortKey();
    const dir = this.sortDir();
    const sorted = [...enabledFiltered].sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (key === 'Enabled') {
        const aEn = a.Enabled === true ? 1 : 0;
        const bEn = b.Enabled === true ? 1 : 0;
        av = aEn; bv = bEn;
      } else {
        av = String((a as any)[key] ?? '').toLowerCase();
        bv = String((b as any)[key] ?? '').toLowerCase();
      }
      let cmp = 0;
      if (av < bv) cmp = -1;
      else if (av > bv) cmp = 1;
      return dir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  });

  // Add Carrier modal state
  protected readonly showAddDialog = signal<boolean>(false);
  protected readonly adding = signal<boolean>(false);
  protected readonly addError = signal<string | null>(null);

  protected currentBusinessPartner: string = 'FleetBooster';
  protected defaultBusinessPartner: string = 'FleetBooster';
  protected currentBusinessPartnerId: number | null = null;
  protected selectedProviderId: number | null = null;
  private readonly subscriptions = new Subscription();

  constructor(
    private api: CopilotApiService,
    private auth: AuthService,
    private translate: TranslateService,
    private hos: HosCopilotService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    // Load sort config from localStorage (default: Name asc)
    try {
      const raw = localStorage.getItem('carriers:sortConfig');
      if (raw) {
        const parsed = JSON.parse(raw) as { key?: 'Name'|'ApiKey'|'Enabled'|'CopilotEnabled'; dir?: 'asc'|'desc' };
        const normalizedKey = parsed?.key === 'CopilotEnabled' ? 'Enabled' : parsed?.key;
        if (normalizedKey === 'Name' || normalizedKey === 'ApiKey' || normalizedKey === 'Enabled') {
          this.sortKey.set(normalizedKey);
        }
        if (parsed?.dir) { this.sortDir.set(parsed.dir); }
      }
    } catch {}
    this.loadCarriers();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadCarriers(): void {
    this.loading.set(true);
    this.error.set(null);
    this.subscriptions.add(this.auth.user$.subscribe((me) => {
      const meta: any = me ? (me as any)['user_metadata'] : undefined;
      const bp = meta?.BusinessPartner ?? 'FleetBooster';
      const bpIdCandidate = Number(meta?.BusinessPartnerId);
      this.currentBusinessPartnerId = Number.isFinite(bpIdCandidate) && bpIdCandidate > 0 ? bpIdCandidate : null;
      this.defaultBusinessPartner = bp;
      this.currentBusinessPartner = bp;
      this.loading.set(false);
      this.evaluateSelectionStateAndMaybeFetch();
    }));
  }

  private fetchCarriersFor(bp: string, businessPartnerId?: number | null): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getCarriers(bp, businessPartnerId).subscribe({
      next: (list) => {
        const normalized = (list || []).map((carrier) => ({
          ...carrier,
          enabling: false
        }));
        this.carriers.set(normalized as CarrierListElement[]);
        this.loading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.error.set(this.translate.instant('carriers.error_load'));
        this.loading.set(false);
      }
    });
  }

  protected onBusinessPartnerChange(bp: BusinessPartner | null): void {
    const name = (bp?.CompanyName || bp?.Name || this.defaultBusinessPartner);
    this.currentBusinessPartnerId = bp?.Id ?? null;
    this.currentBusinessPartner = name;
    this.selectedProviderId = null;
    this.carriers.set([]);
    this.azugaCarriers.set([]);
    this.evaluateSelectionStateAndMaybeFetch();
  }

  protected onProviderChange(provider: Provider | null): void {
    this.selectedProviderId = provider?.Id ?? null;
    this.carriers.set([]);
    this.azugaCarriers.set([]);
    this.evaluateSelectionStateAndMaybeFetch();
  }

  protected onSearchChange(term: string): void {
    this.searchTerm.set(String(term || ''));
  }

  protected clearSearch(): void {
    this.searchTerm.set('');
  }

  protected onSort(key: 'Name' | 'ApiKey' | 'Enabled'): void {
    const nextDir: 'asc' | 'desc' = this.sortKey() === key ? (this.sortDir() === 'asc' ? 'desc' : 'asc') : 'asc';
    this.sortKey.set(key);
    this.sortDir.set(nextDir);
    try {
      localStorage.setItem('carriers:sortConfig', JSON.stringify({ key, dir: nextDir }));
    } catch {}
  }

  protected toggleCarrier(carrier: CarrierListElement): void {
    if (this.blockedReason() !== null || this.currentMode() !== 'mondo') {
      return;
    }
    carrier.enabling = true;
    const enabled = carrier.Enabled === true;
    const next = !enabled;
    if (this.currentBusinessPartnerId == null) {
      this.error.set(this.translate.instant('bp_selector.select_business_partner'));
      carrier.enabling = false;
      return;
    }
    if (!Number.isFinite(Number(carrier.Id)) || Number(carrier.Id) <= 0) {
      this.error.set(this.translate.instant('carriers.error_change_status'));
      carrier.enabling = false;
      return;
    }
    const payload = { CarrierId: Number(carrier.Id), BusinessPartnerId: this.currentBusinessPartnerId };
    const call$ = enabled
      ? this.api.disableCarrier(payload)
      : this.api.enableCarrier(payload);
    call$.subscribe({
      next: () => {
        carrier.Enabled = next;
        carrier.enabling = false;
        this.hos.loadCarriers(this.currentBusinessPartner, this.currentBusinessPartnerId);
      },
      error: (err) => {
        console.error(err);
        carrier.enabling = false;
        this.error.set(this.translate.instant('carriers.error_change_status'));
      }
    });
  }

  protected openAddDialog(): void {
    if (this.blockedReason() !== null || this.currentMode() !== 'mondo') {
      return;
    }
    this.addError.set(null);
    this.adding.set(false);
    this.showAddDialog.set(true);
  }

  protected closeAddDialog(): void {
    this.showAddDialog.set(false);
    this.adding.set(false);
  }

  protected onSubmitCarrier(payload: { apiKey: string }): void {
    const apiKey = (payload.apiKey || '').trim();
    if (!apiKey) {
      this.addError.set(this.translate.instant('add_carrier_dialog.api_key_required'));
      return;
    }
    if (this.currentBusinessPartnerId == null) {
      this.addError.set(this.translate.instant('bp_selector.select_business_partner'));
      return;
    }
    this.addError.set(null);
    this.adding.set(true);
    this.api.validateCarrier({ ApiKey: apiKey, BusinessPartner: this.currentBusinessPartner, BusinessPartnerId: this.currentBusinessPartnerId }).subscribe({
      next: () => {
        this.adding.set(false);
        this.closeAddDialog();
        this.fetchCarriersFor(this.currentBusinessPartner, this.currentBusinessPartnerId);
        this.hos.loadCarriers(this.currentBusinessPartner, this.currentBusinessPartnerId);
      },
      error: (err) => {
        console.error(err);
        this.adding.set(false);
        this.addError.set(this.translate.instant('carriers.error_validate'));
      }
    });
  }

  protected refreshAzugaCarriers(): void {
    if (this.currentMode() !== 'azuga') {
      return;
    }
    if (this.currentBusinessPartnerId == null) {
      return;
    }
    this.fetchAzugaCarriersFor(this.currentBusinessPartnerId);
  }

  protected getBlockedMessageKey(): string {
    const reason = this.blockedReason();
    if (reason === 'missing_bp') {
      return 'bp_selector.select_business_partner';
    }
    if (reason === 'missing_provider') {
      return 'carriers.provider_blocked_missing';
    }
    return '';
  }

  private evaluateSelectionStateAndMaybeFetch(): void {
    this.error.set(null);
    this.azugaError.set(false);
    this.loading.set(false);
    this.azugaLoading.set(false);

    if (this.currentBusinessPartnerId == null) {
      this.currentMode.set(null);
      this.blockedReason.set('missing_bp');
      return;
    }

    if (this.selectedProviderId == null) {
      this.currentMode.set(null);
      this.blockedReason.set('missing_provider');
      return;
    }

    this.blockedReason.set(null);

    if (this.selectedProviderId === Carriers.MONDO_PROVIDER_ID) {
      this.currentMode.set('mondo');
      this.azugaCarriers.set([]);
      this.fetchCarriersFor(this.currentBusinessPartner, this.currentBusinessPartnerId);
      return;
    }

    if (this.selectedProviderId === Carriers.AZUGA_PROVIDER_ID) {
      this.currentMode.set('azuga');
      this.carriers.set([]);
      this.fetchAzugaCarriersFor(this.currentBusinessPartnerId);
      return;
    }

    this.currentMode.set(null);
    this.blockedReason.set('missing_provider');
  }

  private fetchAzugaCarriersFor(businessPartnerId: number): void {
    this.azugaLoading.set(true);
    this.azugaError.set(false);
    this.api.getAzugaCarriers({
      BusinessPartnerId: businessPartnerId,
      ProviderId: Carriers.AZUGA_PROVIDER_ID
    }).subscribe({
      next: (list) => {
        const mapped = (list || []).map((row) => ({
          id: row.id,
          name: row.carrierName,
          dot: row.carrierDotNumber,
          enabled: row.enabled === true,
          toggling: false
        }));
        this.azugaCarriers.set(mapped);
        this.azugaLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.azugaError.set(true);
        this.azugaLoading.set(false);
        this.toast.error(this.translate.instant('carriers.azuga_error_load'));
      }
    });
  }

  protected toggleAzugaCarrier(carrier: AzugaCarrierListItem): void {
    if (this.currentMode() !== 'azuga' || this.currentBusinessPartnerId == null || carrier.toggling) {
      return;
    }
    if (!Number.isFinite(Number(carrier.id)) || Number(carrier.id) <= 0) {
      this.toast.error(this.translate.instant('carriers.azuga_error_change_status'));
      return;
    }

    const previousEnabled = carrier.enabled === true;
    const nextEnabled = !previousEnabled;
    this.patchAzugaCarrierItem(carrier.id, { enabled: nextEnabled, toggling: true });

    this.api.setAzugaCarrierStatus({
      BusinessPartnerId: this.currentBusinessPartnerId,
      ProviderId: Carriers.AZUGA_PROVIDER_ID,
      CarrierId: Number(carrier.id),
      Enabled: nextEnabled
    }).subscribe({
      next: () => {
        this.patchAzugaCarrierItem(carrier.id, { toggling: false });
      },
      error: (err) => {
        console.error(err);
        this.patchAzugaCarrierItem(carrier.id, { enabled: previousEnabled, toggling: false });
        this.toast.error(this.translate.instant('carriers.azuga_error_change_status'));
      }
    });
  }

  private patchAzugaCarrierItem(carrierId: number, changes: Partial<AzugaCarrierListItem>): void {
    this.azugaCarriers.update((list) =>
      (list || []).map((item) => item.id === carrierId ? { ...item, ...changes } : item)
    );
  }
}
